import React, { useState, useEffect, useCallback } from 'react';
import { Bell, Search, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, CheckCircle, XCircle, Clock } from 'lucide-react';
import api from '../../utils/api';
import { useToast } from '../../components/common/Toast';
import './Notifications.css';

const NOTIFICATION_TYPES = [
  '', 'request_submitted', 'request_approved', 'request_denied',
  'request_cancelled_approved', 'request_on_behalf', 'request_removed',
  'request_unaddressed', 'bonus_awarded', 'birthday_reminder',
  'pto_year_reminder_60', 'pto_year_reminder_30', 'pto_year_reset',
];

function Notifications() {
  const toast = useToast();
  const [notifications, setNotifications] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  // Filters
  const [typeFilter, setTypeFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const limit = 50;

  // Expanded rows
  const [expandedRows, setExpandedRows] = useState(new Set());

  const loadNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (typeFilter) params.set('type', typeFilter);
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
      params.set('limit', limit);
      params.set('offset', (page - 1) * limit);

      const res = await api.get(`/admin/notifications?${params.toString()}`);
      setNotifications(res.notifications || []);
      setTotal(res.total || 0);
    } catch (err) {
      setNotifications([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [typeFilter, startDate, endDate, page]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  const toggleRow = (id) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const totalPages = Math.ceil(total / limit);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const getStatusIcon = (notification) => {
    if (notification.is_email_sent) return <CheckCircle size={14} className="notif-status-sent" />;
    if (notification.is_digest) return <Clock size={14} className="notif-status-pending" />;
    return <XCircle size={14} className="notif-status-failed" />;
  };

  const getStatusLabel = (notification) => {
    if (notification.is_email_sent) return 'Sent';
    if (notification.is_digest) return 'Pending (digest)';
    return 'Pending';
  };

  return (
    <div className="notifications-page">
      <div className="page-header">
        <Bell size={22} strokeWidth={2} />
        <h1>Notification Log</h1>
        <span className="total-count">{total} notifications</span>
      </div>

      {/* Filters */}
      <div className="notif-filters">
        <select className="filter-select" value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}>
          <option value="">All Types</option>
          {NOTIFICATION_TYPES.filter(Boolean).map(t => (
            <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
          ))}
        </select>
        <input
          type="date"
          className="filter-date"
          value={startDate}
          onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
        />
        <input
          type="date"
          className="filter-date"
          value={endDate}
          onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
        />
      </div>

      {/* Table */}
      <div className="notif-table-card">
        {loading ? (
          <p className="notif-loading">Loading...</p>
        ) : notifications.length === 0 ? (
          <p className="notif-empty">No notifications found.</p>
        ) : (
          <>
            <div className="notif-table">
              <div className="notif-table-header">
                <span>Date</span>
                <span>Type</span>
                <span>Recipient</span>
                <span>Subject</span>
                <span>Status</span>
                <span></span>
              </div>
              {notifications.map(notif => (
                <React.Fragment key={notif.id}>
                  <div className="notif-table-row" onClick={() => toggleRow(notif.id)}>
                    <span className="notif-date">{formatDate(notif.created_at)}</span>
                    <span className="notif-type">{notif.type.replace(/_/g, ' ')}</span>
                    <span className="notif-recipient">{notif.recipient_name || notif.recipient_email || '-'}</span>
                    <span className="notif-subject">{notif.subject}</span>
                    <span className="notif-status">
                      {getStatusIcon(notif)}
                      <span>{getStatusLabel(notif)}</span>
                    </span>
                    <span className="notif-expand">
                      {expandedRows.has(notif.id) ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </span>
                  </div>
                  {expandedRows.has(notif.id) && (
                    <div className="notif-details-row">
                      <div className="notif-detail-label">Body:</div>
                      <div className="notif-detail-body">{notif.body}</div>
                      {notif.related_entity_type && (
                        <div className="notif-detail-meta">
                          Related: {notif.related_entity_type}
                          {notif.related_entity_id && ` (${notif.related_entity_id.substring(0, 8)}...)`}
                        </div>
                      )}
                      {notif.email_sent_at && (
                        <div className="notif-detail-meta">Sent at: {formatDate(notif.email_sent_at)}</div>
                      )}
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="notif-pagination">
                <button
                  className="pagination-btn"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >
                  <ChevronLeft size={14} /> Previous
                </button>
                <span className="pagination-info">Page {page} of {totalPages}</span>
                <button
                  className="pagination-btn"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                >
                  Next <ChevronRight size={14} />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Notifications;
